import { useEffect, useState } from "react";
import { Button } from "src/apps/components/button/Button";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { Input } from "src/apps/components/inputs/Input";
import { BoldHeader1 } from "src/apps/components/typography/Texts";

import { useTranslations } from "src/apps/hooks/useTranslations";
import { IdNumberValidatorFactory } from "src/contexts/_shared/domain/validation/IdNumberValidatorFactory";
import { IdNumberCountries } from "src/contexts/documentationArea/domain/IdNumberCountries";
import styled from "styled-components";
import { useUpdateBasicData } from "src/apps/hooks/useUpdateBasicData";
import { ActorBasicData } from "src/contexts/_shared/domain/ActorBasicData";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { FormContainer } from "./FormContainer";
import { DocumentationAreaNotFoundError } from "src/contexts/documentationArea/domain/errors/DocumentationAreaNotFoundError";
import { IdNumberTypes } from "src/contexts/documentationArea/domain/IdNumberTypes";
import { DocumentationAreaTypes } from "src/contexts/documentationArea/domain/DocumentationAreaTypes";
import { ImArrowRight2 } from "react-icons/im";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { Select } from "src/apps/components/select/Select";
import {
  AnalyticsEvents,
  useSendAnalyticEvent,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";
import { colors } from "@permisso/permisso-components";
import { TEST_IDS } from "src/apps/config/testIds";
import { useNavigateToErrorScreen } from "src/apps/hooks/useNavigateToErrorScreen";

const TITLE = "documentation_area.sign_up_form.title";
const BUTTON_TEXT = "common.continue";
const NAME_INPUT_TITLE = "documentation_area.sign_up_form.input.name";
const FIRST_SURNAME_INPUT_TITLE = "documentation_area.sign_up_form.input.first_surname";
const SECOND_SURNAME_INPUT_TITLE = "documentation_area.sign_up_form.input.second_surname";
const ID_NUMBER_INPUT_TITLE = "documentation_area.sign_up_form.input.id_number";
const NAME_INPUT_PLACEHOLDER = "documentation_area.sign_up_form.input.name.placeholder";
const FIRST_SURNAME_INPUT_PLACEHOLDER =
  "documentation_area.sign_up_form.input.first_surname.placeholder";
const SECOND_SURNAME_INPUT_PLACEHOLDER =
  "documentation_area.sign_up_form.input.second_surname.placeholder";
const ID_NUMBER_INPUT_PLACEHOLDER = "documentation_area.sign_up_form.input.id_number.placeholder";
const ID_NUMBER_ERROR = "documentation_area.sign_up_form.input.id_number.error";

const ERROR_MAP: { [key: string]: string } = {
  ACTOR_ID_NUMBER_UNIQUE_ERROR:
    "documentation_area.sign_up_form.input.id_number.unique_by_actor.error",
};
const ACTOR_TEMPLATE_SELECTOR_TITLE = "documentation_area.sign_up_form.input.actor_template";
const ACTOR_TEMPLATE_SELECTOR_PLACEHOLDER =
  "documentation_area.sign_up_form.input.actor_template.placeholder";

export const BasicDataForm = () => {
  const { documentationArea, actorBasicData } = useDocumentationArea();
  const { mutate: updateBasicData, isPending: isUpdateBasicDataPending } = useUpdateBasicData();
  const { t } = useTranslations();
  const { sendEvent } = useSendAnalyticEvent();
  const navigateToErrorScreen = useNavigateToErrorScreen();

  if (!documentationArea) return null;

  const actor = documentationArea.getActor();
  const representative = documentationArea.getActor()?.getIdentity()?.getRepresentative();
  const prefilledRepresentative =
    actorBasicData?.prefilledData?.identity.representative ??
    actor.getPrefilledData()?.identity.representative;
  const availableTemplates = actorBasicData?.availableTemplates ?? [];

  const idNumbers = prefilledRepresentative?.idNumbers ?? [];
  const idDocument = idNumbers.length > 0 ? idNumbers[0] : null;

  const isNameEditable =
    (!representative?.hasName() && prefilledRepresentative?.name?.editable) ?? true;
  const isFirstSurnameEditable =
    (!representative?.hasFirstSurname() && prefilledRepresentative?.firstSurname?.editable) ?? true;
  const isSecondSurnameEditable =
    (!representative?.hasSecondSurname() && prefilledRepresentative?.secondSurname?.editable) ??
    true;
  const isIdNumberEditable = idDocument?.data?.number?.editable ?? true;

  const [name, setName] = useState<string>(
    representative?.hasName()
      ? representative.getName()!
      : (actor.getName() ?? prefilledRepresentative?.name?.value ?? ""),
  );
  const [firstSurname, setFirstSurname] = useState<string>(
    representative?.hasFirstSurname()
      ? representative.getFirstSurname()!
      : (actor.getFirstSurname() ?? prefilledRepresentative?.firstSurname?.value ?? ""),
  );
  const [secondSurname, setSecondSurname] = useState<string>(
    representative?.hasSecondSurname()
      ? representative.getSecondSurname()!
      : (actor.getSecondSurname() ?? prefilledRepresentative?.secondSurname?.value ?? ""),
  );
  const [idNumber, setIdNumber] = useState<string>(idDocument?.data?.number?.value ?? "");
  const [selectedActorTemplateId, setSelectedActorTemplateId] = useState<string>(
    availableTemplates.length === 1 ? availableTemplates[0].id : "",
  );

  const [idNumberError, setIdNumberError] = useState<string>("");

  useEffect(() => {
    if (availableTemplates.length === 1) {
      setSelectedActorTemplateId(availableTemplates[0].id);
      return;
    }

    if (
      selectedActorTemplateId &&
      !availableTemplates.some(
        (availableTemplate) => availableTemplate.id === selectedActorTemplateId,
      )
    ) {
      setSelectedActorTemplateId("");
    }
  }, [availableTemplates, selectedActorTemplateId]);

  const idNumberType = idDocument?.type.value
    ? (idDocument!.type.value as IdNumberTypes)
    : IdNumberTypes.ID_CARD;
  const idNumberCountry =
    (idDocument?.data?.country?.value as IdNumberCountries) ?? IdNumberCountries.ES;

  const validateIdNumber = () => {
    if (!idNumberType) return false;
    const validator = IdNumberValidatorFactory.create(
      idNumberCountry,
      DocumentationAreaTypes.INDIVIDUAL,
    );
    const isValid = validator.validate({ value: idNumber, idNumberType });
    if (!isValid) {
      setIdNumberError(ID_NUMBER_ERROR);
    }
    return isValid;
  };

  const isTemplateSelectionRequired = availableTemplates.length > 1;
  const buttonDisabled =
    !name ||
    !firstSurname ||
    !idNumber ||
    !idNumberType ||
    (isTemplateSelectionRequired && !selectedActorTemplateId);
  const validateForm = () => {
    const isIdNumberValid = validateIdNumber();
    return isIdNumberValid && name && firstSurname && idNumber && idNumberType;
  };

  function onSubmitForm() {
    const isFormValid = validateForm();
    if (!isFormValid) {
      return;
    }

    if (!documentationArea) throw new DocumentationAreaNotFoundError();

    const basicData: ActorBasicData = {
      representative: {
        name: name.trim(),
        firstSurname: firstSurname.trim(),
        secondSurname: secondSurname?.trim(),
        idNumber: {
          number: idNumber.trim(),
          type: idNumberType,
          country: idNumberCountry,
        },
      },
      selectedActorTemplateId: selectedActorTemplateId || undefined,
    };

    const selectedTemplate = availableTemplates.find(
      (availableTemplate) => availableTemplate.id === selectedActorTemplateId,
    );

    updateBasicData(
      { actorId: documentationArea.getActor().getId(), basicData },
      {
        onSuccess: () => {
          if (!selectedTemplate) {
            return;
          }

          sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_ACTOR_UPDATED_FROM_TEMPLATE, {
            actor_template_id: selectedTemplate.id,
            actor_template_key: selectedTemplate.key,
            has_it_been_selected_by_the_user: availableTemplates.length > 1,
          });
        },
        onError: (error) => {
          if (!(error instanceof HTTPError)) {
            return;
          }

          const errorCode = error.getCode();
          const mappedErrorKey = errorCode ? ERROR_MAP[errorCode] : null;

          if (mappedErrorKey) {
            setIdNumberError(mappedErrorKey);
            return;
          }

          navigateToErrorScreen(error);
        },
      },
    );
  }

  return (
    <FormContainer data-testid={TEST_IDS.basicData.form}>
      <Icon size={ICON_SIZES.s30} color={colors.grey800} path={ICON_PATHS.ID_CARD} />
      <BoldHeader1>{t(TITLE)}</BoldHeader1>
      <Form>
        <Input
          dataTestId={TEST_IDS.basicData.nameInput}
          title={t(NAME_INPUT_TITLE)}
          placeholder={t(NAME_INPUT_PLACEHOLDER)}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          blocked={!isNameEditable}
        />
        <Input
          dataTestId={TEST_IDS.basicData.firstSurnameInput}
          title={t(FIRST_SURNAME_INPUT_TITLE)}
          placeholder={t(FIRST_SURNAME_INPUT_PLACEHOLDER)}
          value={firstSurname}
          onChange={(e) => {
            setFirstSurname(e.target.value);
          }}
          blocked={!isFirstSurnameEditable}
        />
        <Input
          dataTestId={TEST_IDS.basicData.secondSurnameInput}
          title={t(SECOND_SURNAME_INPUT_TITLE)}
          placeholder={t(SECOND_SURNAME_INPUT_PLACEHOLDER)}
          value={secondSurname}
          onChange={(e) => {
            setSecondSurname(e.target.value);
          }}
          blocked={!isSecondSurnameEditable}
        />
        <Input
          dataTestId={TEST_IDS.basicData.idNumberInput}
          title={t(ID_NUMBER_INPUT_TITLE)}
          placeholder={t(ID_NUMBER_INPUT_PLACEHOLDER)}
          value={idNumber}
          onChange={(e) => {
            setIdNumberError("");
            setIdNumber(e.target.value);
          }}
          error={t(idNumberError)}
          blocked={!isIdNumberEditable}
        />
        {availableTemplates.length > 1 ? (
          <Select
            title={t(ACTOR_TEMPLATE_SELECTOR_TITLE)}
            placeholder={t(ACTOR_TEMPLATE_SELECTOR_PLACEHOLDER)}
            value={selectedActorTemplateId}
            options={availableTemplates.map((availableTemplate) => ({
              key: t(availableTemplate.key),
              value: availableTemplate.id,
            }))}
            setSelectedValue={setSelectedActorTemplateId}
            color={selectedActorTemplateId ? colors.grey700 : colors.grey500}
          />
        ) : null}
      </Form>
      <Button
        dataTestId={TEST_IDS.basicData.continueButton}
        rightIcon={<ImArrowRight2 />}
        onClick={onSubmitForm}
        isLoading={isUpdateBasicDataPending}
        text={t(BUTTON_TEXT)}
        disabled={buttonDisabled}
      />
    </FormContainer>
  );
};

const Form = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1rem;
`;
